/**
 * Announcement Types
 * 
 * Types for site-wide announcement popups managed from the admin dashboard.
 */

export interface Announcement {
  id: string;
  title: string;
  subtitle?: string;
  bulletPoints: string[]; // Supports **bold** markdown syntax
  ctaText: string;
  ctaUrl: string;
  dismissText?: string;
  icon: AnnouncementIcon;
  accentColor: AnnouncementAccentColor;
  // Status
  enabled: boolean;
  // Scheduling (endDate required)
  startDate?: string; // ISO date string - optional, defaults to now
  endDate: string;    // ISO date string - required
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  // Analytics
  impressions: number;
  clicks: number;
}

export type AnnouncementIcon = 'gift' | 'megaphone' | 'party' | 'zap' | 'star' | 'bell';
export type AnnouncementAccentColor = 'green' | 'blue' | 'purple' | 'amber';

// Computed status based on enabled flag and dates
export type AnnouncementStatus = 'draft' | 'scheduled' | 'active' | 'expired';

export function getAnnouncementStatus(announcement: Announcement): AnnouncementStatus {
  if (!announcement.enabled) return 'draft';
  
  const now = new Date();
  const startDate = announcement.startDate ? new Date(announcement.startDate) : new Date(0);
  const endDate = new Date(announcement.endDate);
  
  if (now < startDate) return 'scheduled';
  if (now > endDate) return 'expired';
  return 'active';
}

export interface AnnouncementFormData {
  title: string;
  subtitle: string;
  bulletPoints: string[];
  ctaText: string;
  ctaUrl: string;
  dismissText: string;
  icon: AnnouncementIcon;
  accentColor: AnnouncementAccentColor;
  enabled: boolean;
  startDate: string;
  endDate: string;
}

// Character limits
export const ANNOUNCEMENT_LIMITS = {
  title: 60,
  subtitle: 120,
  bulletPoint: 80,
  maxBulletPoints: 4,
  ctaText: 30,
  dismissText: 30,
};

// Default values for new announcements
export const DEFAULT_ANNOUNCEMENT_FORM: AnnouncementFormData = {
  title: '',
  subtitle: '',
  bulletPoints: [],
  ctaText: 'Learn More',
  ctaUrl: '',
  dismissText: 'Maybe later',
  icon: 'megaphone',
  accentColor: 'green',
  enabled: false,
  startDate: '',
  endDate: '',
};

// Show delay is fixed at 3 seconds - not configurable
export const ANNOUNCEMENT_SHOW_DELAY_MS = 3000;

